import React, { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import FarmacoService from '../../services/farmacoService';
import './FarmacoModal.css';

const FarmacoModal = ({ 
  isOpen = false, 
  onClose = () => {}, 
  onFarmacoCreated = () => {},
  initialData = {},
  searchTerm = ''
}) => {
  const auth = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    components: initialData.components || '',
    dosis: initialData.dosis || '',
    commercialName: initialData.commercialName || '',
    administrationForm: initialData.administrationForm || ''
  });
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !isLoading) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.components.trim()) {
      newErrors.components = 'Los componentes son requeridos';
    }
    
    if (!formData.dosis.trim()) {
      newErrors.dosis = 'La dosis es requerida';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const token = auth.user?.access_token;
      const response = await FarmacoService.createFarmaco(formData, token);
      
      // Call the callback with the newly created farmaco
      onFarmacoCreated(response.data || response);
      
      // Close the modal
      onClose();
      
      // Reset form
      setFormData({
        components: '',
        dosis: '',
        commercialName: '',
        administrationForm: ''
      });
      setErrors({});
    } catch (error) {
      console.error('Error creating farmaco:', error);
      setErrors({
        submit: 'Error al crear el fármaco. Por favor, intenta nuevamente.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      components: '',
      dosis: '',
      commercialName: '',
      administrationForm: ''
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay farmaco-modal-overlay">
      <div className="modal farmaco-modal">
        <div className="modal-header">
          <h3>Agregar Nuevo Fármaco</h3>
          <button 
            className="modal-close" 
            onClick={handleClose}
            disabled={isLoading}
          >
            ✕
          </button>
        </div>
        
        <div className="modal-form">
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="components">
                Componentes <span className="required">*</span>
              </label>
              <input
                type="text"
                id="components"
                name="components"
                value={formData.components}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="Ej: Amoxicilina, Ibuprofeno, etc."
                className={errors.components ? 'error' : ''}
                disabled={isLoading}
              />
              {errors.components && (
                <span className="error-message">{errors.components}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="dosis">
                Dosis <span className="required">*</span>
              </label>
              <input
                type="text"
                id="dosis"
                name="dosis"
                value={formData.dosis}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="Ej: 500mg, 2%, 1ml, etc."
                className={errors.dosis ? 'error' : ''}
                disabled={isLoading}
              />
              {errors.dosis && (
                <span className="error-message">{errors.dosis}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="commercialName">
                Nombre Comercial
              </label>
              <input
                type="text"
                id="commercialName"
                name="commercialName"
                value={formData.commercialName}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="Ej: Amoxil, Advil, etc."
                disabled={isLoading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="administrationForm">
                Forma de Administración
              </label>
              <input
                type="text"
                id="administrationForm"
                name="administrationForm"
                value={formData.administrationForm}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="Ej: Oral, Intravenosa, Tópica, etc."
                disabled={isLoading}
              />
            </div>
          </div>

          {errors.submit && (
            <div className="error-message submit-error">
              {errors.submit}
            </div>
          )}

          <div className="modal-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={handleSubmit}
              disabled={isLoading}
            >
              {isLoading ? 'Creando...' : 'Crear Fármaco'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FarmacoModal;
